"use client"
import { Helmet } from "react-helmet"
import { useState, useEffect, useRef } from "react"
import { useFastTypewriter } from "../../hooks/useFastTypewriter"
import {
  GraduationCap,
  BookOpen,
  MoveIcon,
  RotateCcw,
  Zap,
  Hammer,
  MessageSquare,
  Code,
  TrendingUp,
  Check,
  Database,
  Brain,
  LineChart,
  ArrowRight,
  Calendar,
  Home,
  Users,
  Briefcase,
  FileText,
  Settings,
  ZoomIn,
} from "lucide-react"
import ThreeSphere from "../AMyteSphere/ThreeSphere"
import Header from "./Header"
import Footer from "./Footer"

/* Hero Section - OPTIMIZED */
function HeroSection({ zoomMultiplier }) {
  const typedElementRef = useRef(null)
  const [showTooltip, setShowTooltip] = useState(false)
  const sphereRef = useRef(null)

  useFastTypewriter(typedElementRef, setShowTooltip)

  // Show tooltip after typewriter effect completes
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => {
        setShowTooltip(true)
      }, 1000)

      const hideTimer = setTimeout(() => {
        setShowTooltip(false)
      }, 3000)

      return () => {
        clearTimeout(timer)
        clearTimeout(hideTimer)
      }
    }
  }, [showTooltip])

  return (
    <section
      id="hero"
      className="pt-40 pb-8 px-4 sm:px-6 bg-gradient-to-b from-[#0e1d34] to-[#1e3a8a] overflow-hidden min-h-[500px] lg:min-h-[600px] relative"
    >
      <div className="max-w-7xl mx-auto relative h-full">
        <div className="flex flex-col lg:flex-row items-center justify-between h-full">
          {/* Left: Text Content */}
          <div className="w-full lg:w-[66%] text-center lg:text-left animate-fadeInUp z-20">
            <span className="bg-[#3080c1]/20 text-[#3080c1] rounded-full px-4 py-1 text-sm font-medium">
              Build, Mold & Steer Your Digital Future
            </span>

            <h1 className="mt-4 text-4xl md:text-6xl font-bold text-white leading-tight">
              Custom AI Integration & Workflow Automation
            </h1>

           <div className="text-xl md:text-2xl text-blue-200 my-6 leading-snug text-center lg:text-left">
              <p>Empower your business with</p>
              <p className="h-[40px] md:h-[48px] lg:h-[52px] overflow-hidden">
                <span ref={typedElementRef} className="text-[#3080c1] font-semibold block"></span>
              </p>
            </div>

            <div className="flex flex-wrap sm:flex-nowrap gap-4 justify-center lg:justify-start">
              <a
                href="#solutions"
                className="border border-[#3080c1] px-6 py-3 rounded-lg text-[#3080c1] hover:bg-[#3080c1] hover:text-white transition duration-300 text-center"
              >
                Explore Solutions
              </a>
              <a
                href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-lg transition duration-300 flex items-center gap-2 justify-center"
              >
                Schedule a Discovery Call
                <Calendar className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Right: Sphere Container */}
            <div
              className="relative mt-8 lg:mt-0 lg:absolute lg:right-0 lg:top-16 lg:bottom-0 lg:w-[33%] lg:h-full group overflow-visible"
              ref={sphereRef}
            >
              <div className="relative w-[320px] h-[360px] sm:w-[360px] sm:h-[400px] md:w-[420px] md:h-[440px] mx-auto lg:h-[500px] lg:w-[500px] lg:mx-0">
                {/* Group wrapper that moves entire sphere + effects upward */}
                <div className="relative w-full h-full -translate-y-12 sm:-translate-y-16 md:-translate-y-24 lg:-translate-y-32 flex items-center justify-center">

                  {/* Pulsing ring effect */}
                  <div className="absolute inset-0 rounded-full border-4 border-[#3080c1]/30 animate-ping opacity-30"></div>

                  {/* Glowing background effect */}
                  <div className="absolute inset-0 rounded-full bg-[#3080c1]/10 filter blur-xl group-hover:bg-[#3080c1]/20 transition-all duration-500"></div>

                  {/* Sphere Component */}
                  <div className="relative z-10 w-full h-full transition-transform duration-500 group-hover:scale-105 flex items-center justify-center">
                    <ThreeSphere zoomMultiplier={zoomMultiplier} noAutoTransition />
                  </div>

                  {/* Tooltip (desktop only) */}
                  <div
                    className={`absolute top-[30%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30
                    bg-[#0e1d34]/80 text-white px-4 py-3 rounded-lg border border-[#3080c1]
                    transition-opacity duration-500 whitespace-nowrap text-left
                    ${showTooltip ? "opacity-100" : "opacity-0 pointer-events-none"} hidden lg:flex flex-col gap-2`}
                  >
                    <div className="flex items-center gap-2">
                      <MoveIcon className="w-4 h-4 text-[#3080c1]" />
                      <span className="text-sm"><strong>Shift + Left Click</strong> — Move the sphere</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ZoomIn className="w-4 h-4 text-[#3080c1]" />
                      <span className="text-sm"><strong>Mouse Wheel</strong> — Zoom in/out</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <RotateCcw className="w-4 h-4 text-[#3080c1]" />
                      <span className="text-sm"><strong>Left Click + Drag</strong> — Spin the sphere</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        </div>
      </div>
    </section>
  )
}


// Trusted By Visionaries Component
function TrustedByVisionaries() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    const currentElement = ref.current
    if (currentElement) {
      observer.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement)
      }
    }
  }, [])

  const visionaries = [
    {
      name: "Marco Mascarella",
      company: "Jewish General Hospital",
      description: "Innovator in the HealthTech space revolutionizing workflows with Myte Health Connect",
      avatar: "/Visionaries/MarcoM.jpeg",
      logo: "/Visionaries/MarcoMLogo.jpeg",
      link: "https://www.mcgill.ca/ent/faculty/marco-mascarella-md-msc-phdc",
    },
    {
      name: "Maria Meriano",
      company: "Les Diamants Inc.",
      description: "Pioneering innovative recruitment solutions with AI-Centric Computer Operators",
      avatar: "/Visionaries/MariaM.jpeg",
      logo: "/Visionaries/MariaMLogo.jpeg",
      link: "https://www.les-diamants.com/en/",
    },
    {
      name: "Maria Vilshenski",
      company: "Silo‑IN",
      description: "A Founder and CEO creating AI Centric HR-Tech in construction",
      avatar: "/Visionaries/MariaV.jpeg",
      logo: "/Visionaries/MariaVLogo.jpeg",
      link: "https://silo-in.com",
    },
    {
      name: "Ragheed Johier",
      company: "CourtierXpert",
      description: "Trailbrazer in the real-estate space, digitalizing his workflows with AI",
      avatar: "/Visionaries/RagheedJ.jpeg",
      logo: "/Visionaries/RagheedJLogo.jpeg",
      link: "https://www.courtierxpert.com/home",
    },
  ]

  return (
    <section id="testimonials" ref={ref} className="py-16 px-4 bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className={`text-3xl font-bold mb-4 text-white ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Trusted by Visionaries
          </h2>
          <p className={`text-lg text-blue-200 max-w-3xl mx-auto ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Join the growing community of forward-thinking leaders leveraging our digital intelligence solutions.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {visionaries.map((visionary, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] p-6 rounded-xl border border-[#3080c1]/30 transition-all duration-500 hover:border-[#3080c1] hover:shadow-lg hover:shadow-[#3080c1]/20 hover:-translate-y-1 ${
                isVisible ? "animate-fadeInUp" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <a href={visionary.link} target="_blank" rel="noopener noreferrer" className="block">
                <div className="flex flex-col items-center mb-4">
                  <div className="relative mb-4">
                    <div className="absolute inset-0 bg-[#3080c1]/20 rounded-full blur-lg"></div>
                    <img
                      src={visionary.avatar || "/placeholder.svg"}
                      alt={visionary.name}
                      className="w-24 h-24 rounded-full border-2 border-[#3080c1] object-cover relative z-10"
                    />
                  </div>
                  <img
                    src={visionary.logo || "/placeholder.svg"}
                    alt={`${visionary.company} logo`}
                    className="h-12 w-auto object-contain"
                  />
                </div>
                <h3 className="text-xl font-bold mb-1 text-white text-center">{visionary.name}</h3>
                <p className="text-[#3080c1] mb-3 text-center">{visionary.company}</p>
                <p className="text-blue-200 text-center text-sm">{visionary.description}</p>
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

// Our Proven Process Component
function ProvenProcess() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    const currentElement = ref.current
    if (currentElement) {
      observer.observe(currentElement)
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement)
      }
    }
  }, [])

  const steps = [
    {
      title: "Personalized Consultation",
      description:
        "We begin by understanding your unique challenges, goals, and workflows to craft a tailored digital strategy.",
      icon: <MessageSquare className="h-10 w-10 text-white" />,
    },
    {
      title: "Tailored Development & Deployment",
      description:
        "Build and deploy customized AI-driven infrastructure that seamlessly integrates into your existing operations.",
      icon: <Code className="h-10 w-10 text-white" />,
    },
    {
      title: "Empowering Training & Support",
      description:
        "Hands-on training designed to equip your team with the skills to independently manage and scale your new systems.",
      icon: <GraduationCap className="h-10 w-10 text-white" />,
    },
    {
      title: "Continuous Optimization & Scaling",
      description: "Ongoing strategic support to ensure your solutions grow alongside your evolving business needs.",
      icon: <TrendingUp className="h-10 w-10 text-white" />,
    },
  ]

  return (
    <section id="process" ref={ref} className="py-20 px-4 bg-white">
      <div className="max-w-6xl mx-auto text-center mb-12">
        <h2 className={`text-4xl font-bold text-[#0e1d34] mb-4 ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
          Our Proven Process
        </h2>
        <p className={`text-gray-600 text-lg max-w-3xl mx-auto ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
          Like builders creating homes you can independently maintain, we equip you to master digital autonomy.
        </p>
      </div>

      <div className="relative max-w-5xl mx-auto">
        {steps.map((step, index) => (
          <div
            key={step.title}
            className={`mb-10 flex justify-between items-center w-full ${
              index % 2 === 0 ? "flex-row" : "flex-row-reverse"
            } ${isVisible ? "animate-fadeInUp" : "opacity-0"}`}
            style={{ animationDelay: `${index * 0.2}s` }}
          >
            <div className="order-1 w-5/12"></div>

            <div className="z-10 flex items-center order-1 bg-[#3080c1] shadow-xl w-12 h-12 rounded-full">
              <h1 className="mx-auto text-white font-semibold">{index + 1}</h1>
            </div>

            <div className="order-1 bg-white rounded-lg shadow-xl w-5/12 px-6 py-4 transition transform hover:scale-105">
              <div className="mb-3 inline-block rounded-full bg-[#3080c1] p-3 shadow">{step.icon}</div>
              <h3 className="mb-2 font-bold text-[#0e1d34] text-xl">{step.title}</h3>
              <p className="text-sm text-gray-600">{step.description}</p>
            </div>
          </div>
        ))}
        <div className="absolute left-1/2 transform -translate-x-1/2 top-0 h-full border-l-2 border-dashed border-[#3080c1]/40"></div>
      </div>

      <div className="text-center mt-16">
        <a
          href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
          target="_blank"
          rel="noopener noreferrer"
          className={`inline-flex items-center gap-2 bg-[#3080c1] text-white py-3 px-6 rounded-lg shadow transition duration-300 hover:bg-[#246399] hover:scale-105 ${
            isVisible ? "animate-fadeIn" : "opacity-0"
          }`}
        >
          Schedule a Call <Calendar className="w-5 h-5" />
        </a>
      </div>
    </section>
  )
}

// Real-World Success Stories Component
function SuccessStories() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )
   // Snapshot the current element referenced by ref
      const currentElement = ref.current
      if (currentElement) {
        observer.observe(currentElement)
      }

      return () => {
        if (currentElement) {
          observer.unobserve(currentElement)
        }
      }
    }, [])

  const stories = [
    {
      title: "Myte Cody",
      description: "Enterprise-grade ERP System for end-to-end software development management.",
      link: "https://MyteCody.com",
      icon: <Code className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "Myte Construction",
      description: "Transform Jobsite Paperwork Into Spoken Commands",
      link: "https://mytegroup.ai/myteconstruction",
      icon: <Hammer className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "Myte Social",
      description: "Computer Operator to turn laptops into intelligent robots.",
      link: "https://mytegroup.com/myte-social",
      icon: <Brain className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "Myte Health Connect",
      description: "Patient-Doctor ERP system empowering patients and automating clinical documentation.",
      link: "https://play.google.com/store/apps/details?id=com.myte.myteHeath&hl=en_CA",
      icon: <Users className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "CourtierXpert",
      description: "Digital platform connecting real-estate buyers and sellers efficiently.",
      link: "https://CourtierXpert.com",
      icon: <Home className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "BuilderBios",
      description: "Personalized CV and cover letter builder and career recommendation system.",
      link: "https://builderbios.com",
      icon: <FileText className="h-8 w-8 text-[#3080c1]" />,
    },
  ]

  return (
    <section id="success-stories" ref={ref} className="py-16 px-4 bg-[#0e1d34]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className={`text-3xl font-bold mb-4 text-white ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Real-World Success Stories
          </h2>
          <p className={`text-lg text-blue-200 max-w-3xl mx-auto ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Explore our portfolio of successful digital intelligence solutions across various industries.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stories.map((story, index) => (
            <div
              key={story.title}
              className={`bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 ${
                isVisible ? "animate-fadeInUp" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-[#3080c1]/10 p-3 rounded-full mr-4">{story.icon}</div>
                  <h3 className="text-xl font-bold text-[#0e1d34]">{story.title}</h3>
                </div>
                <p className="text-gray-600 mb-4">{story.description}</p>
                <a
                  href={story.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-[#3080c1] text-[#3080c1] hover:bg-[#3080c1]/10 px-4 py-2 rounded-md inline-flex items-center transition-all duration-300"
                >
                  Learn More <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
            target="_blank"
            rel="noopener noreferrer"
            className={`bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-md inline-block transition-all duration-300 ${isVisible ? "animate-fadeIn" : "opacity-0"}`}
          >
            Schedule a Call <Calendar className="ml-2 inline-block w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  )
}

// Our AI Solutions Section
function AISolutions() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    // Snapshot the current element referenced by ref
      const currentElement = ref.current
      if (currentElement) {
        observer.observe(currentElement)
      }

      return () => {
        if (currentElement) {
          observer.unobserve(currentElement)
        }
      }
    }, [])

  const solutions = [
    {
      title: "Smart System Integration",
      description:
        "Effortlessly unify all your digital tools and workflows into one cohesive AI-powered system for seamless productivity.",
      icon: <Database className="h-10 w-10 text-[#3080c1]" />,
    },
    {
      title: "AI-Powered Workflow Automation",
      description:
        "Eliminate routine tasks and optimize complex processes through intelligent, self-improving automation.",
      icon: <Zap className="h-10 w-10 text-[#3080c1]" />,
    },
    {
      title: "Advanced Voice Control Systems",
      description:
        "Interact intuitively with your digital infrastructure using powerful voice-driven interfaces, transforming everyday operations.",
      icon: <MessageSquare className="h-10 w-10 text-[#3080c1]" />,
    },
    {
      title: "Fully Customized Enterprise AI",
      description:
        "Expertly tailored digital intelligence platforms built specifically to scale with your unique business objectives.",
      icon: <Briefcase className="h-10 w-10 text-[#3080c1]" />,
    },
  ]

  return (
    <section id="solutions" ref={ref} className="py-16 px-4 bg-[#f0f4f9]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className={`text-3xl font-bold mb-4 text-[#0e1d34] ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Digital Intelligence Integration & Automation
          </h2>
          <p className={`text-lg text-gray-700 max-w-3xl mx-auto ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Built For and By Humans with Humans Always in the Loop.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutions.map((solution, index) => (
            <div
              key={solution.title}
              className={`bg-white p-6 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 ${
                isVisible ? "animate-fadeInUp" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start">
                <div className="bg-[#3080c1]/10 p-3 rounded-full mr-4 flex-shrink-0">{solution.icon}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-[#0e1d34]">{solution.title}</h3>
                  <p className="text-gray-600">{solution.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
            target="_blank"
            rel="noopener noreferrer"
            className={`bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-md inline-block transition-all duration-300 ${isVisible ? "animate-fadeIn" : "opacity-0"}`}
          >
            Schedule a Call <Calendar className="ml-2 inline-block w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  )
}

// Enhanced Pricing Model Section
function PricingModel() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    // Snapshot the current element referenced by ref
      const currentElement = ref.current
      if (currentElement) {
        observer.observe(currentElement)
      }

      return () => {
        if (currentElement) {
          observer.unobserve(currentElement)
        }
      }
    }, [])

  const features = [
    "Dedicated Full-Stack Developer",
    "Access to AI Engineer, System Architect & Designer (as needed)",
    "Unlimited changes within subscription period",
    "Full IP ownership of developed code & documentation",
    "Regular Git repository updates",
    "No fixed project timeline; month-to-month flexibility",
    "Continuous consultation & development support",
    "Optional hands-on AI training courses",
    "10 hours complimentary post-subscription support",
  ]

  const additionalNotes = [
    "Subscription fees exclude third-party costs (e.g., AI tokens, APIs, cloud services), billed monthly at cost plus 15% administrative fee if managed by Myte Group.",
    "Cancel anytime with 10 business days' written notice before renewal.",
    "Post-subscription deliverables include complete documentation, schemas, setup instructions, and credentials within 5 business days.",
  ]

  return (
    <section id="pricing" ref={ref} className="py-20 px-4 bg-[#f9fafb]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className={`text-4xl font-bold text-[#0e1d34] mb-4 ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Simple, Transparent & Flexible Pricing
          </h2>
          <p className={`text-lg text-gray-700 max-w-3xl mx-auto ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            A complete digital intelligence solution empowering your enterprise with full autonomy and clear terms.
          </p>
        </div>

        <div
          className={`bg-white p-10 rounded-2xl shadow-xl border-2 border-[#3080c1] max-w-3xl mx-auto ${isVisible ? "animate-fadeInUp" : "opacity-0"}`}
        >
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-[#0e1d34]">Enterprise AI Subscription</h3>
            <div className="text-4xl font-bold text-[#3080c1] mt-2">
              $8,500<span className="text-xl font-normal text-gray-600"> /month</span>
            </div>
          </div>

          <div className="bg-[#3080c1]/10 p-4 rounded-lg mb-6">
            <p className="text-center text-lg font-medium text-[#0e1d34]">Fully Managed AI Development & Integration</p>
          </div>

          <ul className="space-y-3 mb-6">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start">
                <Check className="w-5 h-5 text-[#3080c1] mt-1 mr-3" />
                <span className="text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-300 mb-6">
            <h4 className="font-semibold text-yellow-700 mb-2">Important Notes:</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-yellow-700">
              {additionalNotes.map((note, idx) => (
                <li key={idx}>{note}</li>
              ))}
            </ul>
          </div>

          <div className="text-center mb-6">
            <h4 className="font-semibold text-[#0e1d34]">Optional Training Suite</h4>
            <p className="text-gray-700 text-sm">
              Equip your team with practical AI skills.{" "}
              <span className="font-semibold">Starting at $400 per participant.</span>
            </p>
            <p className="text-gray-600 text-sm italic">Discounts available for bundled training packages.</p>
          </div>

          <p className="text-gray-600 mb-8 text-center">
            Have unique or extensive requirements? Contact us directly for customized pricing.
          </p>

          <div className="text-center">
            <a
              href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#3080c1] hover:bg-[#246399] text-white px-8 py-3 rounded-lg text-lg font-medium transition duration-300 transform hover:scale-105"
            >
              Book Your Free Consultation <Calendar className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

// Training Courses Section
function TrainingCourses() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

     // Snapshot the current element referenced by ref
      const currentElement = ref.current
      if (currentElement) {
        observer.observe(currentElement)
      }

      return () => {
        if (currentElement) {
          observer.unobserve(currentElement)
        }
      }
    }, [])

  const courses = [
    {
      title: "AI Prompt Engineering",
      description: "Master the art of crafting effective prompts for AI systems.",
      icon: <BookOpen className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "Digital Workflow Design",
      description: "Learn to design efficient AI-powered workflows for your business.",
      icon: <LineChart className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "AI System Management",
      description: "Develop skills to maintain and scale your digital intelligence infrastructure.",
      icon: <Settings className="h-8 w-8 text-[#3080c1]" />,
    },
    {
      title: "Enterprise AI Integration",
      description: "Strategies for integrating AI solutions across your organization.",
      icon: <Database className="h-8 w-8 text-[#3080c1]" />,
    },
  ]

  return (
    <section id="training" ref={ref} className="py-16 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className={`text-3xl font-bold mb-4 text-[#0e1d34] ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Myte AI Training Courses
          </h2>
          <p className={`text-lg text-gray-700 max-w-3xl mx-auto ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
            Empower your team with the knowledge and skills to independently manage your digital intelligence systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {courses.map((course, index) => (
            <div
              key={course.title}
              className={`bg-white p-6 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-all duration-300 ${
                isVisible ? "animate-fadeInUp" : "opacity-0"
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="mb-4 bg-[#3080c1]/10 p-3 rounded-full inline-block">{course.icon}</div>
              <h3 className="text-xl font-bold mb-2 text-[#0e1d34]">{course.title}</h3>
              <p className="text-gray-600 mb-4">{course.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <a
            href="/MyteAITrainingCourses"
            className={`bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-md inline-block transition-all duration-300 ${isVisible ? "animate-fadeIn" : "opacity-0"}`}
          >
            Explore Myte AI Training Courses <ArrowRight className="ml-2 inline-block w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  )
}

// Enhanced About Section Component
function AboutSection() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

     // Snapshot the current element referenced by ref
      const currentElement = ref.current
      if (currentElement) {
        observer.observe(currentElement)
      }

      return () => {
        if (currentElement) {
          observer.unobserve(currentElement)
        }
      }
    }, [])

  return (
    <section id="about" ref={ref} className="py-20 px-4 bg-gradient-to-br from-[#f8f9fc] to-[#e2e8f0]">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Side Content */}
          <div>
            <h2 className={`text-4xl font-bold mb-6 text-[#0e1d34] ${isVisible ? "animate-fadeInUp" : "opacity-0"}`}>
              Digital Autonomy Through Integration & Training
            </h2>
            <p
              className={`text-lg text-gray-700 mb-4 ${isVisible ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.2s" }}
            >
              At Myte Group, we believe in empowering our clients to become digitally autonomous. Like builders creating
              houses that homeowners can maintain themselves, we design and implement digital intelligence solutions
              that you can independently manage and scale.
            </p>
            <p
              className={`text-lg text-gray-700 ${isVisible ? "animate-fadeInUp" : "opacity-0"}`}
              style={{ animationDelay: "0.4s" }}
            >
              Our goal is to automate ourselves out of a long-term job by providing you with both the tools and the
              knowledge to take full control of your digital infrastructure.
            </p>
            <div className={`mt-8 ${isVisible ? "animate-fadeInUp" : "opacity-0"}`} style={{ animationDelay: "0.6s" }}>
              <a
                href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#3080c1] hover:bg-[#246399] text-white px-6 py-3 rounded-lg shadow transition-all duration-300 hover:scale-105"
              >
                Schedule a Call <Calendar className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Right Side Vision/Mission Cards */}
          <div
            className={`flex flex-col gap-6 ${isVisible ? "animate-fadeIn" : "opacity-0"}`}
            style={{ animationDelay: "0.8s" }}
          >
            <div className="relative p-6 bg-gradient-to-br from-[#1e3a8a] to-[#0e1d34] rounded-xl shadow-lg border border-[#3080c1]/40 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-[#3080c1]/20 blur-xl"></div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6 text-[#3080c1]" /> Our Vision
                </h3>
                <p className="text-blue-100">
                  We envision a world where businesses have complete control over their digital intelligence
                  infrastructure, with the ability to adapt and scale without ongoing dependency on external providers.
                </p>
              </div>
            </div>

            <div className="relative p-6 bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] rounded-xl shadow-lg border border-[#3080c1]/40 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-full bg-[#3080c1]/20 blur-xl"></div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-[#3080c1]" /> Our Mission
                </h3>
                <p className="text-blue-100">
                  To integrate cutting-edge digital intelligence solutions while training our clients to independently
                  manage these systems, ensuring long-term autonomy and success.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// Enhanced Final CTA Component
function FinalCta() {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

     // Snapshot the current element referenced by ref
      const currentElement = ref.current
      if (currentElement) {
        observer.observe(currentElement)
      }

      return () => {
        if (currentElement) {
          observer.unobserve(currentElement)
        }
      }
    }, [])

  return (
    <section id="final-cta" ref={ref} className="py-24 px-4 bg-[#0e1d34] text-white">
      <div className="max-w-5xl mx-auto text-center">
        <h2 className={`text-4xl md:text-5xl font-bold mb-5 ${isVisible ? "animate-fadeIn" : "opacity-0"}`}>
          Take Control of Your Digital Future Today
        </h2>
        <p
          className={`text-xl md:text-2xl text-blue-200 mb-10 max-w-3xl mx-auto ${isVisible ? "animate-fadeIn" : "opacity-0"}`}
        >
          Partner with Myte Group to craft your personalized AI-powered roadmap and become digitally autonomous.
        </p>
        <div
          className={`flex flex-col md:flex-row gap-6 justify-center items-center ${isVisible ? "animate-fadeInUp" : "opacity-0"}`}
        >
          <a
            href="https://calendly.com/ahmed-mekallach/generative_ai_discovery_call"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#3080c1] hover:bg-[#246399] text-white px-8 py-4 rounded-lg shadow-md text-lg font-semibold transition duration-300 hover:scale-105"
          >
            Book Your Free Consultation <Calendar className="w-5 h-5" />
          </a>
          <a
            href="https://mytecody.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-lg shadow-md text-lg font-semibold transition duration-300 hover:bg-white hover:text-[#0e1d34] hover:scale-105"
          >
            Start Your Project Roadmap <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  )
}

// Add animation styles
const animationStyles = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes ping {
    75%, 100% {
      transform: scale(1.5);
      opacity: 0;
    }
  }

  .animate-fadeIn {
    animation: fadeIn 0.5s ease-in-out forwards;
  }

  .animate-fadeInUp {
    animation: fadeInUp 0.5s ease-in-out forwards;
  }

  .animate-ping {
    animation: ping 2s cubic-bezier(0, 0, 0.2, 1) infinite;
  }
`

// Main HomePage Component
export default function HomePage() {
  const [zoomMultiplier, setZoomMultiplier] = useState(10.5)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [activeSection, setActiveSection] = useState("hero")

  useEffect(() => {
    // Add animation styles to head
    const styleElement = document.createElement("style")
    styleElement.innerHTML = animationStyles
    document.head.appendChild(styleElement)

    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll("section[id]")
      const scrollPosition = window.scrollY + 100

      sections.forEach((section) => {
        const sectionTop = section.offsetTop
        const sectionHeight = section.offsetHeight
        const sectionId = section.getAttribute("id") || ""

        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId)
        }
      })
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const updateZoom = () => {
      if (window.innerWidth <= 640) {
        setZoomMultiplier(4.5) // Small mobile - increased for better visibility
      } else if (window.innerWidth <= 768) {
        setZoomMultiplier(5.5) // Mobile - enhanced size
      } else if (window.innerWidth <= 1024) {
        setZoomMultiplier(6.0) // Tablet - better proportion
      } else {
        setZoomMultiplier(7.5) // Desktop
      }
    }

    updateZoom()
    window.addEventListener("resize", updateZoom)
    return () => window.removeEventListener("resize", updateZoom)
  }, [])

   return (
    <>
      <Helmet>
        <title>Myte Group – AI System Integrators & Digital Autonomy Experts</title>
        <meta name="description" content="Custom AI integration and OpenAI-powered automation for enterprises. Built by Ahmed Mekallach and the Myte Group team." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://mytegroup.ai/" />
        <meta property="og:title" content="Myte Group – AI Integration Experts" />
        <meta property="og:description" content="Helping organizations achieve digital autonomy through custom AI-powered systems and strategic training." />
        <meta property="og:url" content="https://mytegroup.ai/" />
        <meta property="og:image" content="https://mytegroup.ai/og-thumbnail.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Myte Group – AI Integration Experts" />
        <meta name="twitter:description" content="AI system design, OpenAI workflows, and automation strategy by Myte Group." />
        <meta name="twitter:image" content="https://mytegroup.ai/og-thumbnail.png" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#0e1d34] to-[#1e3a8a] text-white overflow-x-hidden">
        <Header
          isMenuOpen={isMenuOpen}
          setIsMenuOpen={setIsMenuOpen}
          activeSection={activeSection}
        />
        <main>
          <HeroSection zoomMultiplier={zoomMultiplier} />
          <AboutSection />
          <ProvenProcess />
          <AISolutions />
          <SuccessStories />
          <TrainingCourses />
          <PricingModel />
          <TrustedByVisionaries />
          <FinalCta />
        </main>
        <Footer />
      </div>
    </>
  ) // ✅ This closes return

}

